import React, { useState, useEffect } from 'react';
import { X, Loader2, Building2, CheckCircle, Search, Globe, Store } from 'lucide-react';
import { userAPI } from '../utils/api';

function B2BApplicationModal({ isOpen, onClose, user }) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    business_name: '',
    business_number: '',
    postal_code: '',
    address: '',
    detail_address: '',
    sales_channel: '',
    sales_platform: '',
    inquiry: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // 유저 정보로 자동 입력
  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        name: user.name || '',
        phone: user.phone || '',
        postal_code: user.saved_address?.postal_code || '',
        address: user.saved_address?.address || '',
        detail_address: user.saved_address?.detail_address || '',
      }));
    }
  }, [user]);

  const handleSearchAddress = () => {
    new window.daum.Postcode({
      oncomplete: function(data) {
        setForm(prev => ({
          ...prev,
          postal_code: data.zonecode,
          address: data.roadAddress || data.jibunAddress,
        }));
      }
    }).open();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name || !form.phone) {
      setError('이름과 전화번호는 필수입니다.');
      return;
    }

    if (!form.address) {
      setError('배송 주소를 입력해주세요.');
      return;
    }

    if (!form.sales_channel) {
      setError('판매 채널을 선택해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await userAPI.submitB2BApplication(form);
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm({
      name: '',
      phone: '',
      business_name: '',
      business_number: '',
      postal_code: '',
      address: '',
      detail_address: '',
      sales_channel: '',
      sales_platform: '',
      inquiry: '',
    });
    setError('');
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  // 신청 완료 화면
  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">신청이 완료되었습니다!</h2>
          <p className="text-gray-600 mb-6">
            담당자가 확인 후 <span className="font-semibold text-purple-600">3영업일 이내</span>에<br />
            문자 또는 전화로 연락드리겠습니다.
          </p>
          <button
            onClick={handleClose}
            className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition-colors"
          >
            확인
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-2">
            {/* <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div> */}
            <div>
              <h2 className="text-lg font-bold text-gray-900">B2B 회원 신청</h2>
              {/* <p className="text-xs text-gray-500">대량 구매 특별 혜택</p> */}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* 기본 정보 */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">기본 정보</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">이름 *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none text-sm"
                  placeholder="홍길동"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">연락처 *</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none text-sm"
                  placeholder="01012345678"
                  disabled
                  required
                />
              </div>
            </div>
          </div>

          {/* 사업자 정보 */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">사업자 정보 <span className="font-normal text-gray-400">(선택)</span></h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">상호명</label>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none text-sm"
                  placeholder="레이어블"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">사업자등록번호</label>
                <input
                  type="text"
                  value={form.business_number}
                  onChange={(e) => setForm({ ...form, business_number: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none text-sm"
                  placeholder="123-45-67890"
                />
              </div>
            </div>
          </div>

          {/* 배송 주소 */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">배송 주소 *</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.postal_code}
                readOnly
                className="w-24 px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-sm"
                placeholder="우편번호"
              />
              <button
                type="button"
                onClick={handleSearchAddress}
                className="px-4 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Search className="w-4 h-4" />
                검색
              </button>
            </div>
            <input
              type="text"
              value={form.address}
              readOnly
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-100 text-sm"
              placeholder="주소 검색 버튼을 눌러주세요"
            />
            <input
              type="text"
              value={form.detail_address}
              onChange={(e) => setForm({ ...form, detail_address: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none text-sm"
              placeholder="상세주소"
            />
          </div>

          {/* 판매 채널 */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">판매 정보 *</h3>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setForm({ ...form, sales_channel: 'online' })}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                  form.sales_channel === 'online'
                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-400 shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Globe className="w-5 h-5" />
                <span>온라인</span>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, sales_channel: 'offline' })}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all ${
                  form.sales_channel === 'offline'
                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-400 shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Store className="w-5 h-5" />
                <span>오프라인</span>
              </button>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                판매처 {form.sales_channel === 'online' ? '(예: 스마트스토어, 쿠팡 등)' : '(예: 매장명, 위치 등)'}
              </label>
              <input
                type="text"
                value={form.sales_platform}
                onChange={(e) => setForm({ ...form, sales_platform: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none text-sm"
                placeholder={form.sales_channel === 'online' ? '스마트스토어, 쿠팡 마켓플레이스' : '강남구 소재 편집샵'}
              />
            </div>
          </div>

          {/* 추가 문의사항 */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">추가 문의사항 <span className="font-normal text-gray-400">(선택)</span></h3>
            <textarea
              value={form.inquiry}
              onChange={(e) => setForm({ ...form, inquiry: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none text-sm resize-none"
              rows="3"
              placeholder="궁금하신 점이나 요청사항을 입력해주세요."
            ></textarea>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <div className="p-3 bg-red-50 rounded-lg text-red-700 text-sm border border-red-200">
              {error}
            </div>
          )}

          {/* 제출 버튼 */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-600 text-white py-3.5 rounded-xl font-semibold hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                신청 중...
              </>
            ) : (
              'B2B 회원 신청하기'
            )}
          </button>

          <p className="text-xs text-gray-400 text-center">
            신청 정보는 B2B 회원 심사 목적으로만 사용됩니다.
          </p>
        </form>
      </div>
    </div>
  );
}

export default B2BApplicationModal;
